/* eslint-disable @typescript-eslint/default-param-last */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { injectable } from 'inversify';
import { validateOrReject, validate, ValidationError } from 'class-validator';
import { plainToClass } from 'class-transformer';
import { at } from 'lodash';

import { FileType } from '../file-type';
import { AppError } from '../error';

type AllStrings<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? AllStrings<U>[] : string;
};

type NumberValidator = {
  type: 'number';
  less_than?: number;
  less_than_or_equal?: number;
  greater_than?: number;
  greater_than_or_equal?: number;
  attribute_id: string;
};

interface FileInputValue {
  value: string | File;
  source: string | File;
}

interface UrlSource {
  type: 'url';
  value: string; // server url
}

interface LocalSource {
  type: 'local';
  value: string; // localCacheId
}

type Source = UrlSource | LocalSource;

interface ServerValue {
  // JSON.stringify({ type: 'file', id: attribute.value.id })
  type: 'server';
  value: string;
}

interface CacheValue {
  // JSON.stringify({ type: 'file_cache', id: cacheId, expiredAt: cacheExpiredAt || '' })
  type: 'cache';
  value: string;
}

interface LocalValue {
  // localCacheId
  type: 'local';
  value: string;
}

type Value = ServerValue | CacheValue | LocalValue;

interface BaseMeta {
  mimeType: string;
  size: number;
  localCacheId: string;
  name: string;
}

interface GeneralMeta extends BaseMeta {
  type: 'general';
}

interface ImageMeta extends BaseMeta {
  type: 'image';
  width: number;
  height: number;
}

export type FileMeta = GeneralMeta | ImageMeta;

export interface FileInputValueNext {
  value: Value;
  source: Source;
  meta?: FileMeta;
}

interface Condition {
  attribute_id: string;
  equal?: string[];
  unequal?: string[];
}

const NUMBER_B_IN_MB = 1048576;
const DEFAULT_SIZE_LIMIT = 20;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;

const getImageResolution = (
  file: File,
): Promise<{ width: number; height: number }> => {
  const image: Promise<{ width: number; height: number }> = new Promise(
    (resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    },
  );

  return image;
};

export const validateFile = async (
  file: File,
  message: string,
  fileTypes: string[],
  fileTypeChecker: FileType,
  skipFileTypeValidation?: boolean,
) => {
  const realFileType = skipFileTypeValidation
    ? file.type
    : await fileTypeChecker.fileTypeFromBlob(file);

  const fileType = file?.name.includes('.sig')
    ? 'application/pgp-signature'
    : realFileType;

  return !fileTypes.includes(fileType) ||
    file.size > DEFAULT_SIZE_LIMIT * NUMBER_B_IN_MB
    ? message
    : undefined;
};

export const validateFileNext = async (
  fileMeta: FileMeta,
  message: string,
  fileTypes: string[],
) => {
  const fileType = fileMeta.name.includes('.sig')
    ? 'application/pgp-signature'
    : fileMeta.mimeType;

  return !fileTypes.includes(fileType) ||
    fileMeta.size > DEFAULT_SIZE_LIMIT * NUMBER_B_IN_MB
    ? message
    : undefined;
};

export const errorsToAllStrings = <T>(
  value: T,
  errors: ValidationError[],
): AllStrings<T> => {
  const result = Array.isArray(value)
    ? ([] as (typeof value)[0])
    : ({} as Record<keyof T, unknown>);

  if (!errors || !errors.length) return result;

  errors.forEach(({ property, constraints, children }) => {
    if (!constraints && children && children?.length !== 0) {
      const propertyName: keyof T = property as keyof T;

      const childrenResult = errorsToAllStrings<T[typeof propertyName]>(
        value[propertyName],
        children,
      );

      result[property] = childrenResult;
    } else {
      result[property as keyof T] = constraints
        ? constraints[Object.keys(constraints)[0]]
        : 'Value is invalid';
    }
  });

  return result;
};

@injectable()
export class ValidationService {
  constructor(private readonly fileType: FileType) {}

  /** Проверить объект на соответствие валидационной схеме и в случае несоответствия выбросить исключение
   * @param {Object.<string, any>} dto Валидируемый объект
   * @param {new () => Object.<string, any>} [constructor] Класс, описывающий валидационную схему
   * @param {string[]} [groups] Массив групп, которые встречаются в валидационной схеме
   * @returns {Promise<void>}
   *  */
  async validateOrReject(
    dto: Record<string, any>,
    constructor?: new () => Record<string, any>,
    groups?: string[],
  ): Promise<void> {
    try {
      const value = constructor ? plainToClass(constructor, dto) : dto;

      await validateOrReject(value, { groups });
    } catch (err) {
      throw new AppError('client', {
        code: 400,
        message: 'Bad Request',
        error: 'Bad Request',
      });
    }
  }

  /** Проверить объект на соответствие валидационной схеме и вернуть объект с ошибками
   * @template {class} T
   * @param {Object.<string, any>} dto Валидируемый объект
   * @param {new () => T} [constructor] Класс, описывающий валидационную схему
   * @param {string[]} [groups] Массив групп, которые встречаются в валидационной схеме
   * @returns {Promise<Object>}
   *  */
  async validate<T extends Record<string, any>>(
    dto: Record<string, any>,
    constructor?: new () => T,
    groups?: string[],
  ): Promise<AllStrings<T>> {
    const value = constructor ? plainToClass(constructor, dto) : dto;

    const errors = await validate(value, {
      validationError: { target: false, value: false },
      groups,
    });

    const result = errorsToAllStrings<T>(value as T, errors);

    return result;
  }

  /** Создать функцию для Wysiwyg, которая в случае "пустого" значения выведет заданное сообщение об ошибке
   * @param {string} [message=Обязательное поле] Сообщение об ошибке в случае, если значение "пустое"
   * @returns {(value: any) => string | undefined}
   * */
  requiredWysiwyg =
    (message = 'Обязательное поле') =>
    (value: any) =>
      typeof value === 'undefined' || value === null || !value?.hasContent
        ? message
        : undefined;

  /** Создать функцию, которая в случае "пустого" значения выведет заданное сообщение об ошибке
   * @param {string} [message=Обязательное поле] Сообщение об ошибке в случае, если значение "пустое"
   * @returns {(value: any) => string | undefined}
   * */
  required =
    (message = 'Обязательное поле') =>
    (value?: any) =>
      typeof value === 'undefined' ||
      value === null ||
      (typeof value === 'boolean' && value === false) ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
        ? message
        : undefined;

  /** Определить является ли текущее валидируемое поле обязательным
   * Текущее валидируемое поле зависит от другого поля (определено в options.field).
   * Если значение поля field (из options.field) не присутствует в in (из options.in),
   * то текущее валидируемое поле является обязательным, иначе необязательным.
   * @template T
   * @param {string} [message=Обязательное поле] Сообщение об ошибке
   * @param [options]
   * @param {string} options.field Имя поля, от которого зависит валидируемое поле
   * @param {T[]} options.in Массив значений для поля field
   * @param {'in' | 'ex'} options.operation Тип операции (include или exclude)
   * @returns {(value: any) => string | undefined}
   * */
  requiredIfNotIn =
    <T>(
      message = 'Обязательное поле',
      options: { field: string; in: T[]; operation: 'in' | 'ex' },
    ) =>
    (value: any, allValues?: any) => {
      const isIncludes = options.in.includes(at(allValues, options.field)[0]);

      return allValues &&
        (options.operation === 'ex' ? isIncludes : !isIncludes)
        ? this.required(message)(value)
        : undefined;
    };

  /** Определить является ли текущее валидируемое поле обязательным
   * Текущее валидируемое поле зависит от значений других полей (определены в options.conditions).
   * Поле является обязательным, когда все значения других полей (из options.conditions)
   * соответствуют значениям из equal или не соответствуют значениям unequal,
   * определенным для каждого поля
   * @template T
   * @param {{attribute_id: string; equal?: string[]; unequal?: string[]}[]} [conditions=Обязательное поле] условия валидации
   * @param [options]
   * @param {string} options.prefix Префикс имени поля, от которого зависит валидируемое поле
   * @param {Condition[]} options.conditions Условия валидации
   * @returns {(value: any) => string | undefined}
   * */
  requiredIfMeetsConditions =
    (
      message = 'Обязательное поле',
      options: {
        prefix: string;
        conditions: Condition[];
      },
    ) =>
    (value: any, allValues?: any) => {
      const meetsConditions = options?.conditions.every((condition) => {
        const fieldValue = at(
          allValues,
          `${options.prefix}_attribute_${condition.attribute_id}`,
        )[0];
        const fieldValueArr = Array.isArray(fieldValue)
          ? fieldValue
          : [fieldValue];

        if (condition.equal) {
          return condition.equal.every((item) => fieldValueArr.includes(item));
        } else if (condition.unequal) {
          return condition.unequal.every(
            (item) => !fieldValueArr.includes(item),
          );
        }
      });

      return allValues && meetsConditions
        ? this.required(message)(value)
        : undefined;
    };

  /** Создать функцию, которая будет возвращать предупреждающее сообщение, если заданное строковое значение дублируется
   * @param {string} message Сообщение, указывающее, что значение дублируется
   * @param {{ field: string; notIn: string[] }} options Опции, содержат список для проверки на дубликаты
   * @returns {(value: any) => string | undefined}
   * */
  notInList =
    (
      message = 'Такое название уже существует',
      options: { field: string; notIn: string[] },
    ) =>
    (value: any) => {
      if (typeof value !== 'string') {
        return undefined;
      }

      const sameRole = options.notIn.find(
        (name) => name.toLowerCase() === value.toLowerCase(),
      );

      return sameRole ? message : undefined;
    };

  /**
   * Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если заданное значение не относится к допустимым типам файлов или файл превышает допустимый размер
   * @param {string} message Сообщение, указывающее, что значение имеет недопустимый тип или размер
   * @param {{ field: string; fileTypes: string[] }} options Опции, содержат список допустимых типов
   * @returns {(value: any) => Promise<string | undefined>}
   * */
  requiredIfFile = (
    message = 'Обязательное поле',
    options: {
      field: string;
      fileTypes: string[];
      skipFileTypeValidation?: boolean;
    },
  ) => {
    const fileTypeChecker = this.fileType;

    return async function validateValue(
      value: any,
    ): Promise<string | undefined> {
      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        const res = await value.reduce((error: string | undefined, arrItem) => {
          return error || validateValue(arrItem);
        }, undefined);
        return res;
      } else {
        const result = await validateFile(
          ((value as FileInputValue)?.source as File) || (value as File),
          message,
          options.fileTypes,
          fileTypeChecker,
          options.skipFileTypeValidation,
        );

        return result;
      }
    };
  };

  requiredIfFileNext = (
    message = 'Обязательное поле',
    options: { field: string; fileTypes: string[] },
  ) => {
    return async function validateValue(
      value: any,
    ): Promise<string | undefined> {
      if (Array.isArray(value)) {
        const res = await value.reduce(
          async (error: string | undefined, arrItem) => {
            return (await error) || (await validateValue(arrItem));
          },
          Promise.resolve(undefined),
        );
        return res;
      }

      if (
        !value ||
        typeof value === 'string' ||
        (value as FileInputValueNext)?.value?.type === 'server' ||
        (value as FileInputValueNext)?.source?.type !== 'local' ||
        !(value as FileInputValueNext)?.meta
      )
        return undefined;

      const result = await validateFileNext(
        value.meta,
        message,
        options.fileTypes,
      );

      return result;
    };
  };

  maxValue = (max: number, message: string) => (value: any) =>
    Number(value) > Number(max) ? message : undefined;

  minValue = (min: number, message: string) => (value: any) =>
    value <= Number(min) ? message : undefined;

  /** Определить попадает ли значение в заданный диапазон
   * @param {string} [min=Обязательное поле] Минимальное значение диапазона
   * @param {string} [max=Обязательное поле] Максимальное значение диапазона
   * @param {string} [message=Обязательное поле] Сообщение об ошибке в случае, если значение не попадает в заданный диапазон
   * @returns {(value: any) => string | undefined}
   * */
  rangeValue = (min: number, max: number, message: string) => (value: any) =>
    Number(value) < min || Number(value) > max ? message : undefined;

  /** Создать функуию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   * @param {string} message Сообщение, указывающее, что значение превышает лимит
   * @param {{ field: string; sizeLimit: number }} options Опции, содержат лимит
   * */
  sizeLimit = <T, K extends keyof T = keyof T>(
    message = 'Объем файла слишком большой',
    options: { field: string; sizeLimit: number },
  ) =>
    function validateValue(
      value?: T[K] | FileInputValue,
      allValues?: Partial<T>,
    ): string | undefined {
      const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;

      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      )
        return undefined;

      if (Array.isArray(value)) {
        const res = value.reduce((error: string | undefined, arrItem) => {
          return error || validateValue(arrItem, allValues);
        }, undefined);
        return res;
      } else {
        const currentValueInForm = at(allValues, options.field)[0];
        if (currentValueInForm) {
          return currentValueInForm &&
            ((value as FileInputValue)?.source as File)?.size > sizeLimit
            ? message
            : undefined;
        } else {
          return ((value as FileInputValue).source as File)?.size > sizeLimit
            ? message
            : undefined;
        }
      }
    };

  sizeLimitNext = (
    message = 'Объем файла слишком большой',
    options: { field: string; sizeLimit: number },
  ) =>
    function validateValue(
      value: Record<string, any>,
      allValues: Record<string, any>,
    ): string | undefined {
      const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;
      if (Array.isArray(value)) {
        const res = value.reduce((error: string | undefined, arrItem) => {
          return error || validateValue(arrItem, allValues);
        }, undefined);
        return res;
      }

      if (
        !value ||
        typeof value === 'string' ||
        (value as FileInputValueNext)?.value?.type === 'server' ||
        (value as FileInputValueNext)?.source?.type !== 'local' ||
        !(value as FileInputValueNext)?.meta
      )
        return undefined;

      const currentValueInForm = at(allValues, options.field)[0];
      if (currentValueInForm) {
        const size = (value as FileInputValueNext)?.meta?.size || 0;
        const res =
          currentValueInForm && size > sizeLimit ? message : undefined;

        return res;
      } else {
        const size = (value as FileInputValueNext)?.meta?.size || 0;
        return size > sizeLimit ? message : undefined;
      }
    };

  /**
   * Проверяет, что ширина и высота каждого файла не меньше заданных значений.
   * При несоответствии возвращает сообщение об ошибке для первого невалидного файла.
   * Функция-валидатор, возвращающая строку ошибки или undefined.
   * @param {string} message - Сообщение, добавляемое к ошибке, если разрешение недостаточно.
   * @param {{ minWidth: number; minHeight: number }} options - Минимально допустимые размеры.
   */
  imageResolutionCheck = (
    message: string,
    options: { minWidth: number; minHeight: number },
  ) => {
    return async function validateValue(
      value?: FileInputValue[] | FileInputValue,
    ): Promise<string | undefined> {
      if (
        !value ||
        typeof value === 'string' ||
        typeof (value as FileInputValue)?.source === 'string'
      ) {
        return undefined;
      }

      if (Array.isArray(value)) {
        const res = await value.reduce<Promise<string | undefined>>(
          (error, arrItem) => {
            return error || validateValue(arrItem);
          },
          Promise.resolve(undefined),
        );
        return res;
      } else {
        const { width, height } = await getImageResolution(
          value.source as File,
        );

        if (width < options.minWidth) return message;
        if (height < options.minHeight) return message;
      }
    };
  };

  /** Создать функуию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   * @param {{ field: string; sizeLimit: number, attributeId: string, validator?: NumberValidator }} options Опции, содержат attributeId (id поля) и опционально числовой валидатор
   /** Создать функцию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
   * @param {{ field: string; sizeLimit: number, attributeId: string, validator: NumberValidator }} options Опции, содержат attributeId (id поля) и опционально числовой валидатор
   * */
  moreOrLess =
    (options: {
      field: string;
      attributeId: string;
      validator: NumberValidator;
    }) =>
    (value?: string | undefined) => {
      if (!value) {
        return undefined;
      }

      const isNumber = /^\d+(\.\d+)?$/.test(value);

      const numberWithPoint = !isNaN(+value) && String(value).includes('.');

      if (!isNumber && !numberWithPoint) {
        return 'Данные должны быть числовым значением';
      }

      if (options.validator.greater_than) {
        const numberValue = Number(value);
        return numberValue > options.validator.greater_than
          ? undefined
          : `Число должно быть больше чем ${options.validator.greater_than}`;
      }

      if (options.validator.greater_than_or_equal) {
        const numberValue = Number(value);
        return numberValue >= options.validator.greater_than_or_equal
          ? undefined
          : `Число должно быть больше или равно ${options.validator.greater_than_or_equal}`;
      }

      if (options.validator.less_than) {
        const numberValue = Number(value);
        return numberValue < options.validator.less_than
          ? undefined
          : `Число должно быть меньше чем ${options.validator.less_than}`;
      }

      if (options.validator.less_than_or_equal) {
        const numberValue = Number(value);
        return numberValue <= options.validator.less_than_or_equal
          ? undefined
          : `Число должно быть меньше или равно ${options.validator.less_than_or_equal}`;
      }
    };

  validUrl =
    (message = 'Значение поля должно быть ссылкой') =>
    (value: string | undefined) => {
      if (!value) return undefined;

      try {
        new URL(value);
        return undefined;
      } catch (_) {
        return message;
      }
    };

  maxLength = (max: number, message: string) => (value: any) => {
    if (!value) return undefined;

    return String(value).length > Number(max) ? message : undefined;
  };

  dateNotInPast =
    (message: string, minutes: string) => (value: string | undefined) => {
      if (!value) return undefined;

      let combinedDateTime = new Date(`${value}T23:59`);

      if (minutes && new Date(`${value}T${minutes}`) > combinedDateTime) {
        const combinedDateTimeString = `${value}T${minutes}`;
        combinedDateTime = new Date(combinedDateTimeString);
      }

      return combinedDateTime < new Date() ? message : undefined;
    };

  timeNotInPast =
    (message: string, fieldName: string) =>
    (value: string | undefined, allValues: any) => {
      const date = allValues[fieldName];

      if (!value || new Date(`${date}T23:59`) < new Date()) return undefined;

      const today = new Date();

      let combinedDateTime = new Date(`${today.toDateString()} ${value}:59`);

      if (date && new Date(date) >= combinedDateTime) {
        const combinedDateTimeString = `${date}T${value}`;
        combinedDateTime = new Date(combinedDateTimeString);
      }

      return combinedDateTime < today ? message : undefined;
    };

  isValidTime =
    (message: string) =>
    (value: string | undefined = ''): string | undefined => {
      const [hoursStr = '', minutesStr = ''] = value.split(':');

      const hours = Number(hoursStr);
      const minutes = Number(minutesStr);

      const invalid =
        isNaN(hours) ||
        isNaN(minutes) ||
        hours > MAX_HOURS ||
        minutes > MAX_MINUTES;

      return invalid ? message : undefined;
    };

  /**
   * Создать функцию валидации количества элементов в массиве
   * @param {Object} options Параметры валидации
   * @param {number} [options.min] Минимальное количество элементов
   * @param {number} [options.max] Максимальное количество элементов
   * @param {string} message Сообщение об ошибке
   * @returns {(value: any[] | undefined) => string | undefined}
   */
  arrayLength =
    (message: string, options: { min?: number; max?: number; field: string }) =>
    (value: any[] | undefined) => {
      if (value === undefined || value === null) {
        return undefined;
      }

      if (Array.isArray(value)) {
        const length = value.length;

        if (options.min !== undefined && length < options.min) {
          return message;
        }

        if (options.max !== undefined && length > options.max) {
          return message;
        }
      }

      return undefined;
    };

  /** Определить является ли текущее валидируемое поле больше чем значение из передаваемого в options поля
   * Текущее валидируемое поле зависит от другого поля (определено в options.field).
   * @template T
   * @param {string} [message=Обязательное поле] Сообщение об ошибке
   * @param [options]
   * @param {string} options.field Имя поля, от которого зависит валидируемое поле
   * @returns {(value: any) => string | undefined}
   * */
  maxValueWithConnectedFieldValidator =
    (message = 'Обязательное поле', options: { field: string }) =>
    (value: any, allValues?: any) => {
      const connectedValue = at(allValues, options.field)[0];

      return Number(value) > Number(connectedValue) ? message : undefined;
    };

  /** Создать функцию, которая будет возвращать предупреждающее сообщение,
   * если в тексте отсутствует поддерживаемый протокол
   * @param {string} [message=Ссылка должна содержать поддерживаемый протокол] Сообщение об ошибке
   * @returns {(value: string | undefined) => string | undefined}
   */
  containsSupportedProtocol =
    (message = 'Ссылка должна содержать поддерживаемый протокол') =>
    (value: string | undefined) => {
      if (!value) return undefined;

      const SUPPORTED_PROTOCOLS = [
        'http:',
        'https:',
        'mailto:',
        'sms:',
        'tel:',
      ];

      try {
        const parsedUrl = new URL(value);
        const hasSupportedProtocol = SUPPORTED_PROTOCOLS.includes(
          parsedUrl.protocol,
        );

        if (!hasSupportedProtocol) {
          return message;
        }

        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
          const hasDoubleSlash = value.includes('://');
          return hasDoubleSlash
            ? undefined
            : 'Ссылка должна содержать // после протокола';
        }

        return undefined;
      } catch {
        return message;
      }
    };
}
